import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, LogOut, Mail, Phone, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lq_admin_token";

const inputCls =
  "w-full rounded-xl border border-blush-100 bg-white px-5 py-3.5 text-sm text-ink placeholder:text-ink-soft/50 outline-none transition-colors duration-300 focus:border-blush-500";

function fmtErr(e) {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return "Errore di connessione. Riprova.";
}

function fmtDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [bookings, setBookings] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [dates, setDates] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Errore nel caricamento delle prenotazioni");
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(fmtErr(err));
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setBookings(null);
  };

  const confirm = async (id) => {
    const value = dates[id];
    if (!value) {
      toast.error("Scegli prima data e ora dell'appuntamento");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/bookings/${id}/confirm`,
        { appointment_at: new Date(value).toISOString() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Appuntamento confermato: la cliente ha ricevuto la conferma via email");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Richiesta eliminata");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-cream px-6">
        <motion.form
          onSubmit={login}
          data-testid="admin-login-form"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md rounded-3xl border border-blush-100 bg-white p-10 shadow-xl shadow-blush-100/50"
        >
          <p className="font-serif text-3xl italic text-ink">
            Lash Queen <span className="ml-1 align-middle font-sans text-[10px] font-semibold not-italic uppercase tracking-[0.35em] text-blush-500">Carpi</span>
          </p>
          <p className="mt-2 text-sm text-ink-soft">Area riservata — gestione prenotazioni</p>
          <label htmlFor="admin-pw" className="mb-2 mt-8 block text-[10px] font-semibold uppercase tracking-[0.3em] text-ink-soft">
            Password
          </label>
          <input
            id="admin-pw"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="La tua password"
            className={inputCls}
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-3 text-sm text-red-500">{error}</p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-6 w-full rounded-full bg-ink py-4 text-xs font-semibold uppercase tracking-[0.3em] text-blush-50 transition-colors duration-500 hover:bg-blush-600"
          >
            Accedi
          </button>
          <a href="/" data-testid="admin-back-home" className="mt-6 block text-center text-xs text-ink-soft underline decoration-blush-500 underline-offset-4">
            Torna al sito
          </a>
        </motion.form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream px-6 py-12 lg:px-10">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-serif text-3xl italic text-ink">Le tue prenotazioni</p>
            <p className="mt-1 text-sm text-ink-soft">
              Conferma con data e ora: la cliente riceve la conferma subito e il promemoria automatico il giorno prima.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 rounded-full border border-blush-100 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:border-blush-500"
            >
              <RefreshCw size={14} /> Aggiorna
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-blush-50 transition-colors duration-300 hover:bg-blush-600"
            >
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>

        <div className="mt-10 space-y-6">
          {bookings === null && <p className="text-sm text-ink-soft">Caricamento...</p>}
          {bookings?.length === 0 && (
            <p data-testid="admin-empty-state" className="rounded-2xl border border-blush-100 bg-white p-10 text-center text-sm text-ink-soft">
              Nessuna richiesta per ora. Le nuove prenotazioni appariranno qui.
            </p>
          )}
          {bookings?.map((b) => (
            <motion.div
              key={b.id}
              data-testid={`admin-booking-card-${b.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl border border-blush-100 bg-white p-6 lg:p-8"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-serif text-2xl italic text-ink">{b.name}</p>
                  <p className="mt-1 text-sm font-medium text-blush-600">{b.service}</p>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink-soft">
                    <a href={`tel:${b.phone}`} className="flex items-center gap-1.5 hover:text-blush-600">
                      <Phone size={13} /> {b.phone}
                    </a>
                    {b.email && (
                      <a href={`mailto:${b.email}`} className="flex items-center gap-1.5 hover:text-blush-600">
                        <Mail size={13} /> {b.email}
                      </a>
                    )}
                  </div>
                  {b.message && <p className="mt-3 text-sm italic text-ink-soft">“{b.message}”</p>}
                  <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-ink-soft/70">
                    Ricevuta il {fmtDate(b.created_at)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {b.status === "confermata" ? (
                    <span data-testid={`admin-status-${b.id}`} className="rounded-full bg-blush-50 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-blush-600">
                      Confermata · {fmtDate(b.appointment_at)}
                    </span>
                  ) : (
                    <span data-testid={`admin-status-${b.id}`} className="rounded-full bg-amber-50 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-600">
                      Da confermare
                    </span>
                  )}
                  {b.status === "confermata" && (
                    <span className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">
                      {b.reminder_sent ? "Promemoria inviato" : "Promemoria in programma"}
                    </span>
                  )}
                  <button
                    onClick={() => remove(b.id)}
                    data-testid={`admin-delete-${b.id}`}
                    className="mt-1 flex items-center gap-1.5 text-xs text-ink-soft/70 transition-colors duration-300 hover:text-red-500"
                  >
                    <Trash2 size={13} /> Elimina
                  </button>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-blush-100 pt-5">
                <input
                  type="datetime-local"
                  data-testid={`admin-date-input-${b.id}`}
                  value={dates[b.id] || ""}
                  onChange={(e) => setDates({ ...dates, [b.id]: e.target.value })}
                  className={`${inputCls} w-auto`}
                />
                <button
                  onClick={() => confirm(b.id)}
                  data-testid={`admin-confirm-button-${b.id}`}
                  className="flex items-center gap-2 rounded-full bg-blush-500 px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-blush-600"
                >
                  <CalendarCheck size={14} />
                  {b.status === "confermata" ? "Modifica data" : "Conferma appuntamento"}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
